import { PenaltiesService } from '@fair-play/infra';
import { inject } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { map, Observable, switchMap } from 'rxjs';
import {
  GetUserPenaltiesQueryResult,
  GroupedPenalties,
  SinglePenaltyWithDate
} from './get-user-penalties.query-result';
import { CommonUserPenaltyDto, GetUserPenaltiesPathParams, GetUserPenaltiesResponse, PenaltyReason } from '@core-http';
import { DateFormats, formatDateToString } from '@core';

export class GetUserPenaltiesQueryHandler {
  private readonly penaltiesService: PenaltiesService = inject(PenaltiesService);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);

  getPenalties(): Observable<GetUserPenaltiesQueryResult> {
    return this.activatedRoute.params.pipe(
      switchMap((params: Params): Observable<GetUserPenaltiesResponse> => {
        const pathParams: GetUserPenaltiesPathParams = { userId: params['id'] };
        return this.penaltiesService.getPenalties(pathParams);
      }),
      map((dto: GetUserPenaltiesResponse) => this.mapToQueryResult(dto))
    );
  }

  private mapToQueryResult(dto: GetUserPenaltiesResponse): GetUserPenaltiesQueryResult {
    const { activePenalty, penaltyHistory }: GetUserPenaltiesResponse = dto;

    const allPenalties: CommonUserPenaltyDto[] = [...(activePenalty ? [activePenalty] : []), ...(penaltyHistory ?? [])];

    const groupedPenaltiesMap: Map<PenaltyReason, GroupedPenalties> = new Map<PenaltyReason, GroupedPenalties>();

    for (const penalty of allPenalties) {
      const { penaltyReason, penaltyDate, author }: CommonUserPenaltyDto = penalty;

      if (!groupedPenaltiesMap.has(penaltyReason)) {
        groupedPenaltiesMap.set(penaltyReason, {
          penaltyType: penaltyReason,
          penaltyDetails: []
        });
      }

      const penaltyDetail: SinglePenaltyWithDate = {
        authorName: author.displayName,
        createdAt: formatDateToString(penaltyDate, DateFormats.DayMonthYearDot)
      };

      groupedPenaltiesMap.get(penaltyReason)!.penaltyDetails.push(penaltyDetail);
    }

    const groupedPenaltiesArray: GroupedPenalties[] = Array.from(groupedPenaltiesMap.values());

    return {
      totalPenalties: allPenalties.length,
      groupedPenalties: groupedPenaltiesArray
    };
  }
}
